import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { Plus, FileStack, Sparkles, FileType, FileText, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NewContractMenu = () => {
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ name: "", contract_type: "", country: "", parties: "", brief: "" });
  const [creating, setCreating] = useState(false);
  const wordRef = useRef(null);
  const pdfRef = useRef(null);

  async function createFromAI() {
    if (!aiForm.name.trim() || !aiForm.brief.trim()) { toast.error("Le titre et le brief sont requis"); return; }
    setCreating(true);
    try {
      const res = await fetch(API_URL + "/api/contracts/from-ai", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aiForm)
      });
      if (res.ok) {
        const c = await res.json();
        toast.success("Génération lancée — le contrat s'écrit...");
        setAiOpen(false);
        navigate("/contracts/content/" + c.contract_id);
      } else { const e = await res.json(); toast.error(e.detail || "Erreur"); }
    } catch (e) { toast.error("Erreur de connexion"); }
    setCreating(false);
  }

  async function uploadFile(endpoint, file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.loading("Import en cours...", { id: "imp" });
    try {
      const res = await fetch(API_URL + endpoint, { method: "POST", credentials: "include", body: fd });
      toast.dismiss("imp");
      if (res.ok) {
        const c = await res.json();
        toast.success("Contrat importé");
        navigate("/contracts/content/" + c.contract_id);
      } else { const e = await res.json(); toast.error(e.detail || "Erreur d'import"); }
    } catch (e) { toast.dismiss("imp"); toast.error("Erreur d'import"); }
  }

  return (
    <>
      <input ref={wordRef} type="file" accept=".docx,.doc" className="hidden"
        onChange={(e) => uploadFile("/api/contracts/from-word", e.target.files?.[0])} data-testid="word-file-input" />
      <input ref={pdfRef} type="file" accept=".pdf" className="hidden"
        onChange={(e) => uploadFile("/api/contracts/from-pdf", e.target.files?.[0])} data-testid="pdf-file-input" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="new-contract-btn"><Plus className="w-4 h-4 mr-2" />Nouveau contrat</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => navigate("/templates")} data-testid="new-from-template">
            <FileStack className="w-4 h-4 mr-2" />Depuis un modèle
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setAiOpen(true)} data-testid="new-from-ai">
            <Sparkles className="w-4 h-4 mr-2" />Depuis l'IA
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => wordRef.current?.click()} data-testid="new-from-word">
            <FileType className="w-4 h-4 mr-2" />Depuis un document Word
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => pdfRef.current?.click()} data-testid="new-from-pdf">
            <FileText className="w-4 h-4 mr-2" />Depuis un PDF
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="ai-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-blue-600" />Générer un contrat par IA</DialogTitle>
            <DialogDescription>Décrivez le contrat souhaité, l'IA (Claude Opus) rédige un contrat complet.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titre du contrat *</Label>
              <Input value={aiForm.name} onChange={(e) => setAiForm({ ...aiForm, name: e.target.value })} placeholder="Ex: Contrat de prestation de services" data-testid="ai-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Input value={aiForm.contract_type} onChange={(e) => setAiForm({ ...aiForm, contract_type: e.target.value })} placeholder="prestation, bail, NDA..." />
              </div>
              <div>
                <Label>Pays / droit</Label>
                <Input value={aiForm.country} onChange={(e) => setAiForm({ ...aiForm, country: e.target.value })} placeholder="France, Bénin..." data-testid="ai-country-input" />
              </div>
            </div>
            <div>
              <Label>Parties</Label>
              <Input value={aiForm.parties} onChange={(e) => setAiForm({ ...aiForm, parties: e.target.value })} placeholder="Prestataire: ... ; Client: ..." />
            </div>
            <div>
              <Label>Brief / exigences *</Label>
              <Textarea value={aiForm.brief} onChange={(e) => setAiForm({ ...aiForm, brief: e.target.value })} rows={4} placeholder="Objet, montant, durée, clauses souhaitées (confidentialité, résiliation...)" data-testid="ai-brief-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)}>Annuler</Button>
            <Button onClick={createFromAI} disabled={creating} data-testid="ai-generate-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Générer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewContractMenu;
